import React, { useEffect, useState, useRef } from "react";
import { View, Text, ScrollView, Image, TouchableOpacity, Pressable, useWindowDimensions, Platform, ActivityIndicator } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useHome } from "../../hooks/Home/useHome";
import { useAuth } from "../../context/Auth/AuthContext";
import useTimeTracking from "../../hooks/TimeTracking/useTimeTracking";
import ScreenContainer from "../../components/Containers/ScreenContainer";
import TimeTrackerMini from "../../components/MiniPlayer/TimeTrackerMini";
import TimeCard from "../../components/Cards/TimeCard";
import TimeChart from "../../components/Charts/TimeChart";
import HoursLog from "../../components/TimeTracking/HoursLog";
import TimeDisplay from "../../components/TimeTracking/TimeDisplay";
import TimeTrackingButtons from "../../components/TimeTracking/TimeTrackingButtons";
import TasksSheet, { ClockOutSheetRef } from '../../components/TimeTracking/TasksSheet';
import { getEntries, getWorkingUsers } from "../../services/TimeTracking/api";
import Notification from "../../components/Notification/Notification";
import UserGridSection from "../../components/TimeTracking/UserGridSection";
import { AppUser } from "../../services/TimeTracking/types";

export default function HomeScreen() {
  // const { totalTrackedTime, weeklyHours } = useHome();
  const { width } = useWindowDimensions();
  const isMobile = Platform.OS !== "web" || width < 768;

  const { userInfo } = useAuth();
  const formatTime = (seconds: number): string =>
    new Date(seconds * 1000).toISOString().substr(11, 8);


  const {
    clockInTime,
    clockOutTime,
    elapsedTime,
    handleClockIn,
    handleClockOut,
    handleBreakToggle,
    handleLunchToggle,
    isOnBreak,
    isOnLunch,
    isWorking,
    todaysLogs,
    activeElapsed,
    breakElapsed,
    activeTimeSeconds,
    breakTimeSeconds,
    weeklyTimeSeconds,
    weeklyDailyHours,
    errorNotification,
    dismissNotification
  } = useTimeTracking();

  const [activity, setActivity] = useState("");
  const clockOutSheetRef = useRef<ClockOutSheetRef>(null);

  const formatTasks = (): string | undefined => {
    return activity.trim()
      ? JSON.stringify({ task1: activity })
      : undefined;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const [workingUsers, setWorkingUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getWorkingUsers();
        setWorkingUsers(data);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  return (
    <ScreenContainer>
      <ScrollView contentContainerStyle={{ paddingBottom: 100 }}>
        <Pressable>
        {/* Header */}
        <View className="flex-none md:flex-row items-center justify-between mb-6 px-4">

          <View className="flex-row items-center space-x-4">
            {!isMobile && userInfo?.picture && (
              <Image
                source={{ uri: userInfo.photo || userInfo.picture }} className="w-16 h-16 rounded-full"
              />
            )}
            <View>
              <Text className="text-xl text-gray-500 text-center md:text-left">{getGreeting()},</Text>
              <Text className="text-2xl font-semibold text-center md:text-left">
                {userInfo?.name || "User"} 👋
              </Text>
            </View>
          </View>
          <View>
            <View className="mt-2">
            <TimeTrackingButtons
              handleClockIn={() => handleClockIn(formatTasks())}
              handleClockOut={() => clockOutSheetRef.current?.show()} 
              handleBreakToggle={() => handleBreakToggle(formatTasks())}
              handleLunchToggle={() => handleLunchToggle(formatTasks())}
              isOnBreak={isOnBreak}
              isOnLunch={isOnLunch}
              isWorking={isWorking}
              clockInTime={clockInTime}
              clockOutTime={clockOutTime}
              />
            </View>
          </View>
        </View>

        {/* Summary Cards */}
        <View className="px-4">
        <TimeCard
          elapsedTime={activeElapsed}
          activeTime={elapsedTime}
          breakTime={breakElapsed}
          weeklyTime={formatTime(weeklyTimeSeconds)}
        />
          <TimeChart weeklyHours={weeklyDailyHours} />
        </View>

        {/* Divider */}
        <View className="my-6 h-[1px] bg-gray-300 mx-4" />

        {/* Time Tracking Section */}
        <View className="px-4">
          <HoursLog logEntries={todaysLogs} />
          
        </View>
        
        <View className="px-4">
          {loading ? ( <ActivityIndicator size="large" /> ) : null}
          <UserGridSection title="Now Working" users={ workingUsers } />
        </View>
        <TasksSheet ref={clockOutSheetRef} onClockOut={handleClockOut} />
        </Pressable>
      </ScrollView>
      {errorNotification && (
        <Notification
          title="An error has occurred!"
          message={errorNotification}
          type="error"
          actions={
            <button
              onClick={dismissNotification}
              className="text-sm font-medium text-white bg-red-600 px-3 py-1 rounded hover:bg-red-700"
            >
              Close
            </button>
          }
        />
      )}
    </ScreenContainer>
  );
}
